import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import { useTheme, styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
    Box,
    Button,
    Grid2 as Grid,
    Typography,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    IconButton,
    Zoom,
    Slide,
    Fade,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogContent,
    Tooltip
} from '@mui/material';
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import DownloadIcon from '@mui/icons-material/DownloadForOffline';
import InfoIcon from '@mui/icons-material/Article';
import CheckIcon from '@mui/icons-material/Check';
import CopyIcon from '@mui/icons-material/ContentCopy';
import CloseIcon from '@mui/icons-material/Close';
import PreviousPageIcon from '@mui/icons-material/KeyboardArrowLeft';
import NextPageIcon from '@mui/icons-material/KeyboardArrowRight';

import CREDENTIALS from './content';
import LOCAL_STORAGE from "../../constants/localStorage";
const { CREDENTIALS_NAMESPACE } = LOCAL_STORAGE;
const PAGE_COUNT_MENU = [1, 5, 10, 20];

const StyledGridContainer = styled(Grid)`
    height: 100%;
    align-items: center;
    font-family: poppins;
    min-width: 200px;
    margin: 1vh 10%;
    margin-bottom: 35px;
`;
const StyledCredentialsHeader = styled(Typography)`
    font-family: poppins;
    text-align: center;
    margin: 5vh;
    pointer-events: none;
`;
const StyledCredentialsDetailsDialog = styled(Dialog)`
    height: 100%;
    width: 100%;

    .MuiDialog-paper {
        height: 100%;
        width: 100%;
        max-width: 80%;
        max-height: 90%;
        margin-top: 0;
    }
    .MuiDialog-paper::-webkit-scrollbar {
        display: none;
    }
`;
const StyledCredentialsDetailsModalBox = styled(Box)`
    text-align: center;
    justify-content: center;
    background-color: white;
    height: 100%;
    width: 100%;
    min-width: 250px;
`;
const responsiveDetailsModalBox = {
    top: {
        xs: "2%", 
        md: "2%"
    }, 
    left: {
        xs: "11%", 
        md: "11%"
    }, 
    minWidth: {
        xs: "80%", 
        md: "80%"
    }
}
const StyledCredentialsDownloadButton = styled(Button)`
    display: block;
    margin: 0 auto;
    height: 64px;
    width: 64px;
    transition: all 0.25s;
    color: gray;

    &:hover {
        scale: 1.10;
        color: black;
    }
    &:active {
        top: 1px;
        left: 1px;
        scale: 1.10;
        color: black;
    }
`;
const StyledCredentialsDownloadIcon = styled(DownloadIcon)`
    height: 32px;
    width: 32px;
`;
const StyledCredentialsDetailsHeader = styled(Typography)`
    font-family: poppins;
    font-weight: bold;
    padding-left: 20px;
    padding-right: 10px;
    padding-top: 10px;
`;
const StyledCredentialsDetailsValidationContainer = styled(Grid)`
    margin-left: 25%;
    margin-right: 25%;
`;
const StyledCredentialsDetailsValidationHeader = styled(Typography)`
    font-family: poppins;
    font-weight: bold;
    display: block;
`;

const StyledCredentialsDetailsMarkdown = styled(Markdown)`
    font-family: poppins;
    padding-left: 20px;
    padding-right: 20px;
    padding-bottom: 10px;

    table {
        margin: 0 auto;
    }
    th {
        padding-left: 10px;
        padding-right: 10px;
    }
`;
const StyledCredentialsDetailsConfirmationButton = styled(IconButton)`
    transition: all 0.15s;
    &:hover {
        scale: 1.10;
        color: green;
    }
    &:active {
        top: 1px;
        left: 1px;
        scale: 1.10;
        color: green;
    }
`;
const responsiveDetailsConfirmationIcon = {
    color: {
        xs: "green",
        sm: "green",
        md: "gray"
    },
    '&:hover': {
        color: {
            md: "green"
        }
    }
};
const StyledCredentialsDetailsConfirmationIcon = styled(CheckIcon)`
    height: 40px;
    width: 40px;
`;
const StyledCredentialsDetailsCloseButton = styled(IconButton)`
    position: fixed;
    transition: all 0.15s;
    &:hover {
        scale: 1.10;
        color: red;
    }
    &:active {
        scale: 1.10;
        color: red;
    }
`;
const responsiveDetailsCloseButton = {
    top: {
        xs: "4%"
    },
    right: {
        xs: "8%",
        sm: "10%",
        md: "10.5%",
        lg: "10.5%"
    },
    '&:active': {
        top: {
            xs: "4.1%"
        }
    }
};
const StyledCredentialsDetailsCloseIcon = styled(CloseIcon)`
    height: 32px;
    width: 32px;
`;
const responsiveDetailsCloseIcon = {
    color: {
        xs: "gray",
        sm: "gray",
        md: "gray"
    },
    '&:hover': {
        color: {
            xs: "red",
            sm: "red",
            md: "red"
        }
    },
    '&:active': {
        color: {
            xs: "red",
            sm: "red",
            md: "red"
        }
    },
    height: {
        xs: "24px",
        sm: "24px",
        md: "28px",
        lg: "32px"
    },
    width: {
        xs: "24px",
        sm: "24px",
        md: "28px",
        lg: "32px"
    }
};
const responsiveFilterControl = {
    textAlign: {
        xs: "center",
        sm: "unset",
        md: "unset",
        lg: "unset"
    }
}
const responsiveFormControl = {
    m: 1, 
    minWidth: 120,
    maxWidth: {
        xs: "80%",
        sm: "80%",
        md: "0",
        lg: "0"
    },
    textAlign: {
        xs: "center",
        sm: "left",
        md: "left",
        lg: "left"
    }
};
const responsivePageFiltersMenuItem = {
    justifyContent: {
        xs: "center",
        sm: "flex-start",
        md: "flex-start",
        lg: "flex-start"
    }
};
const StyledPageControlsGridItem = styled(Grid)`
    text-align: right;
`;
const responsivePageControlsGrid = {
    textAlign: {
        xs: "center",
        sm: "right",
        md: "right",
        lg: "right"
    },
    marginRight: {
        xs: "5%",
        sm: "unset",
        md: "unset",
        lg: "unset"        
    }
};
const responsivePageIndicator = {
    pointerEvents: "none",
    paddingRight: {
        xs: "0",
        sm: "17.5px",
        md: "17.5px",
        lg: "17.5px"
    }
}
const StyledPageControlsArrowIcon = styled(IconButton)`
    margin-left: 5px;
    margin-right: 5px;
`;
const responsiveGridPageControlsArrowSingle = {
    textAlign: "center",
    "svg": {
        height: "1.5em",
        width: "1.5em"
    }
}
const responsivePageControlsArrow = {
    marginLeft: {
        xs: "10px",
        sm: "5px",
        md: "5px",
        lg: "5px"
    },
    marginRight: {
        xs: "10px",
        sm: "5px",
        md: "5px",
        lg: "5px"
    }
};
const StyledCredentialsInfoIcon = styled(InfoIcon)`
    transition: all 0.15s;
    &:hover {
        scale: 1.10;
        color: white;
    }
    &:active {
        top: 1px;
        left: 1px;
        scale: 1.10;
    }
`;
const StyledCredentialsCopyIcon = styled(CopyIcon)`
    height: 20px;
    width: 20px;
    padding-left: 15px;
    cursor: pointer;
    cursor: grab;
    cursor: -moz-grab;
    cursor: -webkit-grab;
    transform: translateY(5px);
    transition: all 0.15s;
    &:hover {
        scale: 1.10;
    }
    &:active {
        scale: 1.00;
        cursor: grabbing;
        cursor: -moz-grabbing;
        cursor: -webkit-grabbing;
    }
`;

const responsiveCredentialsCopyIcon = {
    transform: {
        xs: "translate(-7px, 5px)",
        sm: "translateY(0, 5px)"
    }
};

const StyledCredentialsImageListItem = styled(ImageListItem)`
    user-select: none;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -o-user-select: none;
`;

const Credentials = () => {
    let history = useHistory();
      const [windowSize, setWindowSize] = useState({
        height: window.innerHeight,
        width: window.innerWidth
    });
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [modalDetails, setModalDetails] = useState({});
    const [copySuccessful, setCopySuccessful] = useState(false);
    const [orgFilter, setOrgFilter] = useState("all");
    const [pageCount, setPageCount] = useState(() => (parseInt(localStorage.getItem(CREDENTIALS_NAMESPACE.PAGE_COUNT)) || PAGE_COUNT_MENU[PAGE_COUNT_MENU.length - 1]));
    const [page, setPage] = useState(1);
    const [credentials, setCredentials] = useState(CREDENTIALS);
    const theme = useTheme();
    const columnBreakXs = useMediaQuery(theme.breakpoints.down("xs"));
    const columnBreakSm = useMediaQuery(theme.breakpoints.down("sm"));
    const columnBreakMd = useMediaQuery(theme.breakpoints.down("md"));
    const columnBreakLg = useMediaQuery(theme.breakpoints.down("lg"));
    useEffect(() => {
        const handleWindowResize = () => {
            setWindowSize(getWindowDimensions());
        }
        if(!windowSize){
            setWindowSize(getWindowDimensions());
        }
        window.addEventListener("resize", handleWindowResize, {passive: true});

        return () => {
            window.removeEventListener("resize", handleWindowResize);
        }
    }, [windowSize]);
    useEffect(() => {
        if(copySuccessful){
            setTimeout(() => {
                setCopySuccessful(false);
            }, 1000);
        }
    },[copySuccessful]);
    useEffect(() => {
        localStorage.setItem(CREDENTIALS_NAMESPACE.PAGE_COUNT, parseInt(pageCount));
    },[pageCount]);
    useEffect(() => {
        if(history.action !== "POP"){
            window.scrollTo({
                top: 0
            });
        }
    }, [history]);
    useEffect(() => {
        if(page > 1){
            setPage(1);
        }
    }, [orgFilter, pageCount]);
    const copyCodeToClipboard = async (validationCode) => {
        await navigator.clipboard.writeText(validationCode);
        setCopySuccessful(true);
    }
    const handleOpenDetailsModal = (
        credentialTitle, 
        credentialDetails, 
        credentialDownloadUrl,
        credentialValidationUrl,
        credentialValidationCode
    ) => {
        setModalDetails({
            credentialTitle,
            credentialDetails,
            credentialDownloadUrl,
            credentialValidationUrl,
            credentialValidationCode
        });
        setDetailsModalOpen(true);
    }
    const handleCloseDetailsModal = () => {
        setModalDetails({});
        setDetailsModalOpen(false);
    }
    const handleOrgFilterChange = (event) => {
        let newOrgFilter = event.target.value;
        setOrgFilter(newOrgFilter);
        let filteredArr = CREDENTIALS.filter((credential) => (
            credential.organization === newOrgFilter || credential.shortOrg === newOrgFilter || newOrgFilter === "all"
        )).sort((a,b) => (a.order - b.order));
        setCredentials(filteredArr);
    }
    const getOrgsFromArr = (arr) => {
        let orgs = {};
        arr.forEach((item) => {
            let org = item.shortOrg || item.organization;
            orgs[org] = org;
        });
        return Object.keys(orgs);
    }
    const handlePageCountChange = (event) => {
        setPageCount(event.target.value);
    }
    const paginateCredentials = (arr) => {
        return arr.slice(page === 1 ? 0 : pageCount*(page-1), page*pageCount);
    }
    const handlePreviousPageClick = () => {
        setPage(page === 1 ? 1 : page - 1);
    }
    const handleNextPageClick = () => {
        setPage(page + 1);
    }
    const generatePageIndicator = (credentialsLength) => {
        let startNum = 0;
        let endNum = Math.min(page*pageCount, credentialsLength);
        if(page !== 1 && page !== 0){
            startNum = ((pageCount) * (page - 1)) + 1;
        }
        else{
            startNum = 1;
        }

        return pageCount === 1 ? `${startNum} of ${credentials.length}` : `${startNum}-${endNum} of ${credentials.length}`; 
    }
    const getColumns = () => {
        if(columnBreakXs || columnBreakSm){
            return 1
        }
        else if(columnBreakMd || columnBreakLg){
            return 2
        }
        return 3;
    }
    const getWindowDimensions = () => {
        return {
            height: window.innerHeight,
            width: window.innerWidth
        }
    }

    return ( 
        <StyledGridContainer container rowSpacing={2}>
            <StyledCredentialsDetailsDialog open={detailsModalOpen} onClose={handleCloseDetailsModal} scroll="body" TransitionComponent={Slide} direction="up" mountOnEnter unmountOnExit in={detailsModalOpen}>
                <DialogContent>
                    <StyledCredentialsDetailsModalBox sx={responsiveDetailsModalBox}>
                        <Tooltip title="Close" placement="left">
                            <StyledCredentialsDetailsCloseButton onClick={handleCloseDetailsModal} variant="contained" sx={responsiveDetailsCloseButton}>
                                <StyledCredentialsDetailsCloseIcon sx={responsiveDetailsCloseIcon}/>
                            </StyledCredentialsDetailsCloseButton>
                        </Tooltip>
                        <StyledCredentialsDetailsHeader variant={columnBreakXs || columnBreakSm ? "h5" : "h4"}>
                            {modalDetails.credentialTitle}
                            <Tooltip title="Download certificate pdf" followCursor placement="right-start">
                                <StyledCredentialsDownloadButton href={modalDetails.credentialDownloadUrl}>
                                    <StyledCredentialsDownloadIcon/>
                                </StyledCredentialsDownloadButton>
                            </Tooltip>
                        </StyledCredentialsDetailsHeader>
                        <StyledCredentialsDetailsValidationContainer container rowSpacing={2}>
                            <Grid size={{xs: 12, sm: 12, md: 6}}>
                                <StyledCredentialsDetailsValidationHeader variant="h7">
                                    Certificate Validation Link
                                </StyledCredentialsDetailsValidationHeader>
                                <a href={modalDetails.credentialValidationUrl}>{modalDetails.credentialValidationUrl? "Validate" : "N/A"}</a>
                            </Grid>
                            <Grid size={{xs: 12, sm:12, md: 6}}>
                                <StyledCredentialsDetailsValidationHeader variant="h7">
                                    Certificate Validation Code
                                </StyledCredentialsDetailsValidationHeader>
                                {
                                    !modalDetails.credentialValidationCode ? 
                                    "N/A"
                                    : 
                                    <React.Fragment>
                                        {modalDetails.credentialValidationCode}
                                        <Tooltip title={copySuccessful? "Validation Code Copied!" : "Copy Validation Code"} followCursor placement="right-start">
                                            <StyledCredentialsCopyIcon onClick={() => copyCodeToClipboard(modalDetails.credentialValidationCode)} disabled={copySuccessful} sx={responsiveCredentialsCopyIcon}/>
                                        </Tooltip>
                                    </React.Fragment>
                                }
                            </Grid>
                        </StyledCredentialsDetailsValidationContainer>
                        <StyledCredentialsDetailsMarkdown remarkPlugins={[remarkGfm]}>
                            {modalDetails.credentialDetails}
                        </StyledCredentialsDetailsMarkdown>
                        <Tooltip title="Confirm" placement="right-start">
                            <StyledCredentialsDetailsConfirmationButton onClick={handleCloseDetailsModal} variant="contained">
                                <StyledCredentialsDetailsConfirmationIcon sx={responsiveDetailsConfirmationIcon}/>
                            </StyledCredentialsDetailsConfirmationButton>
                        </Tooltip>
                    </StyledCredentialsDetailsModalBox>
                </DialogContent>
            </StyledCredentialsDetailsDialog>
            <Grid size={{xs: 12, sm: 12, md:12 }}>
                <Slide direction="down" in mountOnEnter unmountOnExit timeout={400}>
                    <StyledCredentialsHeader variant={columnBreakXs || columnBreakSm? "h4" : "h3"}>My Credentials</StyledCredentialsHeader>
                </Slide>
            </Grid>
            <Fade in mountOnEnter unmountOnExit timeout={1000}>
                <Grid size={{xs: 12, sm: 6, md:6 }} sx={responsiveFilterControl}>
                    <FormControl variant="standard" sx={responsiveFormControl} fullWidth={columnBreakSm}>
                        <InputLabel id="org-filter-label">Organization</InputLabel>
                        <Select labelId="org-filter-label" label="org-filter" id="org-filter" defaultValue="all" value={orgFilter} onChange={handleOrgFilterChange}>
                            {
                                [   <MenuItem key="all" value="all" sx={responsivePageFiltersMenuItem}>All</MenuItem>
                                    ,
                                    ...getOrgsFromArr(CREDENTIALS).map((org) => (
                                    <MenuItem key={org} value={org} sx={responsivePageFiltersMenuItem}>{org}</MenuItem>
                                ))]
                            }
                        </Select>
                    </FormControl>
                    <FormControl variant="standard" sx={responsiveFormControl} fullWidth={columnBreakSm}>
                        <InputLabel id="count-label">Display</InputLabel>
                        <Select labelId="count-label" label="page-count-label" id="page-count-label" defaultValue={pageCount} value={pageCount} onChange={handlePageCountChange}>
                            {
                                PAGE_COUNT_MENU.map((pageCount) =>(
                                    <MenuItem key={pageCount} value={pageCount} sx={responsivePageFiltersMenuItem}>{pageCount}</MenuItem>
                                ))
                            }
                        </Select>
                    </FormControl>
                </Grid>
            </Fade>
            { (pageCount === 1 && !columnBreakMd) &&
                <React.Fragment>
                    <Fade in mountOnEnter unmountOnExit timeout={1000}>
                        <StyledPageControlsGridItem size={{xs: 12, sm: 6, md:6 }} sx={responsivePageControlsGrid}>
                            <Typography sx={responsivePageIndicator}>{generatePageIndicator(credentials.length)}</Typography>
                        </StyledPageControlsGridItem>
                    </Fade>
                    <Slide direction="right" in mountOnEnter unmountOnExit timeout={750}>
                        <Grid size={{ msm: 2, sm: 2, md: 3, lg: 3.5 }} sx={responsiveGridPageControlsArrowSingle}>
                            <StyledPageControlsArrowIcon onClick={handlePreviousPageClick} disabled={page === 1} sx={responsivePageControlsArrow}>
                                <PreviousPageIcon />
                            </StyledPageControlsArrowIcon>
                        </Grid>
                    </Slide>
                    <Fade in mountOnEnter unmountOnExit timeout={1000}>
                        <Grid size={{msm: 8, sm: 8, md: 6, lg: 5}}>
                                <ImageList cols={pageCount === 1 ? 1 : getColumns()} gap={20}>
                                    {paginateCredentials(credentials).map((credential, idx) => (
                                        <Zoom key={`${credential.title}-1-${idx}`} in timeout={400}>
                                            <StyledCredentialsImageListItem>
                                            <img
                                                srcSet={credential.image}
                                                src={credential.image}
                                                alt={credential.title}
                                                style={{objectFit:"scale-down"}}
                                            />
                                            <ImageListItemBar
                                                title={credential.title}
                                                subtitle={credential.organization}
                                                actionIcon={
                                                    <Tooltip title="More Details" placement="top">
                                                        <IconButton
                                                            sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                                            aria-label={`info about ${credential.title}`}
                                                            onClick={() => handleOpenDetailsModal(`${credential.shortOrg || credential.organization} ${credential.title}`, credential.details, credential.pdf, credential.validationUrl, credential.validationCode)}
                                                        >
                                                            <StyledCredentialsInfoIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                }
                                                />
                                            </StyledCredentialsImageListItem>
                                        </Zoom>
                                ))}
                                </ImageList>
                        </Grid>
                    </Fade>
                    <Slide direction="left" in mountOnEnter unmountOnExit timeout={750}>
                        <Grid size={{ msm: 2, sm: 2, md: 3, lg: 3.5 }} sx={responsiveGridPageControlsArrowSingle}>
                            <StyledPageControlsArrowIcon onClick={handleNextPageClick} disabled={Math.min(page*pageCount, credentials.length) === credentials.length} sx={responsivePageControlsArrow}>
                                <NextPageIcon />
                            </StyledPageControlsArrowIcon>
                        </Grid>
                    </Slide>
                </React.Fragment>
            }
            { (pageCount !== 1 || columnBreakMd) &&
                <React.Fragment>
                    <Fade in mountOnEnter unmountOnExit timeout={1000}>
                        <StyledPageControlsGridItem size={{xs: 12, sm: 6, md:6 }} sx={responsivePageControlsGrid}>
                            <Typography sx={responsivePageIndicator}>{generatePageIndicator(credentials.length)}</Typography>
                            <StyledPageControlsArrowIcon onClick={handlePreviousPageClick} disabled={page === 1} sx={responsivePageControlsArrow}>
                                <PreviousPageIcon />
                            </StyledPageControlsArrowIcon>
                            <StyledPageControlsArrowIcon onClick={handleNextPageClick} disabled={Math.min(page*pageCount, credentials.length) === credentials.length} sx={responsivePageControlsArrow}>
                                <NextPageIcon />
                            </StyledPageControlsArrowIcon>
                        </StyledPageControlsGridItem>
                    </Fade>
                    <Grid size={{xs: 12, sm: 12, md:12 }}>
                            <ImageList cols={pageCount === 1 ? 1 : getColumns()} gap={20}>
                                {paginateCredentials(credentials).map((credential,idx) => (
                                    <Zoom key={`${credential.title}-${idx}`} in timeout={800}>
                                        <StyledCredentialsImageListItem>
                                        <img
                                            srcSet={credential.image}
                                            src={credential.image}
                                            alt={credential.title}
                                            style={{objectFit:"scale-down"}}
                                        />
                                        <ImageListItemBar
                                            title={credential.title}
                                            subtitle={credential.organization}
                                            actionIcon={
                                                <Tooltip title="More Details" placement="top">
                                                    <IconButton
                                                        sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                                                        aria-label={`info about ${credential.title}`}
                                                        onClick={() => handleOpenDetailsModal(`${credential.shortOrg || credential.organization} ${credential.title}`, credential.details, credential.pdf, credential.validationUrl, credential.validationCode)}
                                                    >
                                                        <StyledCredentialsInfoIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                            />
                                        </StyledCredentialsImageListItem>
                                    </Zoom>
                            ))}
                            </ImageList>
                    </Grid>
                </React.Fragment>
            }
        </StyledGridContainer>
    );
}
 
export default Credentials;